import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

// Local storage key for pending invite code (shared with Auth.tsx, localStorage persists across tabs)
const INVITE_CODE_STORAGE_KEY = 'momentumeq_pending_invite_code';
const INVITE_CODE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

const getStoredInviteCode = (): string | null => {
  try {
    const raw = localStorage.getItem(INVITE_CODE_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Check expiry
    if (Date.now() - data.timestamp > INVITE_CODE_EXPIRY_MS) {
      localStorage.removeItem(INVITE_CODE_STORAGE_KEY);
      return null;
    }
    return data.code || null;
  } catch {
    return null;
  }
};

/**
 * SmartRedirect determines where to send authenticated users at root URL:
 * - If pending invite code exists → /auth (to complete redemption)
 * - If journey progress >= 20% (values completed) → /home
 * - If journey progress < 20% → /dashboard (main dashboard with values tab)
 * 
 * Deep links to specific pages bypass this redirect.
 */
export const SmartRedirect = () => {
  const { user, loading: authLoading } = useAuth();
  const [journeyProgress, setJourneyProgress] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  // IMPORTANT: initialize synchronously to avoid a race where we redirect to /home or
  // /dashboard before the invite code is loaded from localStorage after OAuth.
  const [pendingInviteCode] = useState<string | null>(() => getStoredInviteCode());
  const location = useLocation();

  useEffect(() => {
    const checkProgress = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      // If there's a pending invite code, skip progress check - redirect to auth
      if (pendingInviteCode) {
        setLoading(false);
        return;
      }

      // Fast path: brand-new accounts (created <60s ago) always have 0 values — skip DB query
      const createdAt = new Date(user.created_at).getTime();
      if (Date.now() - createdAt < 60_000) {
        setJourneyProgress(0);
        setLoading(false);
        return;
      }

      // Fast path: if localStorage indicates returning user, skip the query
      const lastHome = localStorage.getItem('momentum-last-home');
      if (lastHome === 'home') {
        setJourneyProgress(20);
        setLoading(false);
        return;
      }

      try {
        // Quick check: count core values (>= 3 means values phase complete = 20%+ progress)
        const { data, error } = await supabase
          .from('core_values')
          .select('id')
          .eq('user_id', user.id);

        if (error) {
          console.error('Error checking journey progress:', error);
          setJourneyProgress(0);
        } else {
          // If user has >= 3 core values, they've completed values phase (20%+)
          const coreValuesCount = data?.length || 0;
          setJourneyProgress(coreValuesCount >= 3 ? 20 : 0);
        }
      } catch (err) {
        console.error('Error in progress check:', err);
        setJourneyProgress(0);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      checkProgress();
    }
  }, [user, authLoading, pendingInviteCode]);

  // Store last visited tab preference
  useEffect(() => {
    if (journeyProgress !== null && journeyProgress >= 20) {
      localStorage.setItem('momentum-last-home', 'home');
    }
  }, [journeyProgress]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/welcome" replace />;
  }

  // If there's a pending invite code, redirect to auth page to complete redemption
  if (pendingInviteCode) {
    return <Navigate to={`/auth?code=${encodeURIComponent(pendingInviteCode)}&oauth=true`} replace />;
  }

  // Redirect based on journey progress
  // Users with completed values (>= 20% progress) go to Home
  // New users go to Dashboard to complete values
  if (journeyProgress !== null && journeyProgress >= 20) {
    return <Navigate to="/home" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};
